import { useState, useRef, useEffect, useCallback, useMemo } from "react"
import { ButtonField, InputField, CheckboxField, SelectField, Multiselect, SearchSuggestionsWrapper } from '@wk/components-react16';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { API_USER } from '../../config/constants';
import { Toast } from "../../services"
import useDebounce from "../../hooks/useDebounce";
import HttpService from "../../config/http.service";

const EditUser = ({ data }: any) => {
    const { roleList, projectList, triggerGetUser, selectedUser } = data;
    const { debounce } = useDebounce();
    
    const [initialValues, setInitialValues] = useState({ username: "", password: "", active: "", userType: "USER" });
    const [resetPassword, setResetPassword] = useState(false);
    const [searchText, setSearchText] = useState('');

    const [id, setId] = useState(0);
    const suggestionList: any = useMemo(() => [
        {
            id: "userlist",
            type: "suggestions",
            title: "User suggestions",
            hasIcon: true,
            suggestions: []
        }
    ], [])
    
    const [suggestionUserList, setSuggestionUserList] = useState(suggestionList);
    const searchRef = useRef<SearchSuggestionsWrapper>(null);

    const Schema = yup.object().shape({
        username: yup
            .string(),
        password: yup
            .string()
            .min(8, 'Password must be 8 characters long')
            .matches(/[0-9]/, 'Password must contain at least one number')
            .matches(/[a-z]/, 'Password must contain at least lowercase letter')
            .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
            .matches(/[^\w]/, 'Password must contain at least one special character'),
        active: yup.string(),
        userType: yup.string().required()
    });

    const getUserItem = (u: any) => {
        const userList = suggestionUserList[0]?.suggestions;
        const user = userList?.find((user: any) => user.text.toUpperCase() === u.toUpperCase());
        if (user && user.uId) {
            getUser(user);
        }
    }
    const getUser = useCallback((u: any) => {
        let initialObj: any = { username: "", password: "", active: "" };
        HttpService.getRequest(`${API_USER.GET_USERBYID}?id=${u.uId}`).then(
            (res: any) => {
                if (res) {
                    roleList.forEach((item: any) => {
                        item.isSelected = res.roles.some(
                            (itm: any) => itm.name.toUpperCase() === item.name.toUpperCase()
                        );
                    });
                    if (res.userType === 'ADMIN') {
                        projectList.forEach((item: any) => item.isSelected = false);
                    } else
                    projectList.forEach((item: any) => {
                        item.isSelected = res.projects.some(
                            (itm: any) => itm.id === item.id
                        );
                    });
                    initialObj = { username: res.username, password: "", active: res.active ? "active" : "inactive", userType: "USER" };
                    setInitialValues(initialObj);
                    setId(res.id)
                }
            },
            (err: any) => {
                Toast(err.message, "error")
            },
        )
    }, [projectList, roleList]);

    // ************************* User suggestion code ************************
    const getSuggestion = useCallback((text: string) => {
        setSuggestionUserList([])
        let payload = {
            username: text
        }
        HttpService.postRequest(API_USER.SEARCHUSER, payload).then(
            (res: any) => {
                suggestionList[0].suggestions = res.map((itm: any, index: any) => ({ id: index, text: itm.username, uId: itm.id }));
                setSuggestionUserList(suggestionList);
            },
            (err: any) => {
                Toast(err.message, "error")
            },
        )
    }, [suggestionList]); 

    const getSuggestionRef = useRef(getSuggestion);

    useEffect(() => {
        getSuggestionRef.current = getSuggestion;
    }, [getSuggestion]);

    const userSuggestion = useRef(debounce((text: string) => getSuggestionRef.current(text), 400)).current;

    const getUserSuggestion = (text: string) => {
        if (text === '') return;
        userSuggestion(text)
    }
    // ************************* End of user suggestion code ************************

    const submit = async (values: any) => {
        if (values.username === '') {
            Toast("Please select username", "error")
            return;
        }
        if (resetPassword) {
            let regex = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[^\w\s]).{8,}$/;
            if (!regex.test(values.password)) {
                Toast("Password must be 8+ chars, with 1 digit, 1 lowercase, 1 uppercase, and 1 special character", "error")
                return;
            }
        }
        if (roleList.filter((item: any) => item.isSelected).length === 0) {
            Toast("Please select atleast one role.", "error")
            return;
        }
        if (values.active === '') {
            Toast("Please select the status", "error")
            return;
        }
        if (projectList.filter((item: any) => item.isSelected).length === 0) {
            Toast("Please select atleast one project.", "error")
            return;
        }
        values['roles'] = roleList.filter((item: any) => item.isSelected).map((item: any) => item.id);
        values['projectIds'] = projectList.filter((item: any) => item.isSelected).map((item: any) => item.id);
        values.active = values.active === 'active' ? true : false;
        values['id'] = id;
        if (!resetPassword) delete values["password"];
        HttpService.postRequest(API_USER.UPDATE_USER, values).then(
            (res: any) => {
                if (res.status === 200) Toast("User updated successfully.", "success")
                else Toast(res.message, "success")
                clearForm();
                clearProject();
            },
            (err: any) => {
                Toast(err.message, "error")
            },
        )
    }

    const clearProject = () => {
        projectList.forEach((item: any) => item.isSelected = false);
    }

    const clearForm = () => {
        setInitialValues({ username: "", password: "", active: "", userType: "USER" });
        setId(0)
        setSuggestionUserList([]);
        roleList.forEach((item: any) => item.isSelected = false);
        clearProject();
        setResetPassword(false);
        setSearchText('');
        if (searchRef.current) {
            searchRef.current.onClear();
        }
    }

     useEffect(() => {
       selectedUser && getUser(selectedUser);
    }, [triggerGetUser, selectedUser, getUser]);

    return (
        <div className=" p-20 mb-0 rounded-3 border wk-bg-primary-blue-tint6" style={{ borderColor: "#E6F2F9" }}>
            <div className="wk-row">
                <div className="wk-col-6 relative custom-overflow-inherit" id="search-suggestion">
                    <SearchSuggestionsWrapper ref={searchRef} onSearchClick={getUserItem} onInputChange={getUserSuggestion}
                        onItemSelected={getUser} options={suggestionUserList} size="small" isSearchEnabledOnEmptyInput={false}
                        value={searchText} placeholder="Search by email id">
                    </SearchSuggestionsWrapper >
                </div>
                <div className="wk-col-3">
                    <ButtonField size={'small'}>
                        <button type="button" style={{ marginRight: '5px' }} onClick={clearForm}>
                            Clear
                        </button>
                    </ButtonField>
                </div>
            </div >
            <hr className="mb-10" />
            <Formik
                validationSchema={Schema}
                enableReinitialize={true}
                initialValues={initialValues}
                onSubmit={submit}
            >
                {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isValid,
                    dirty }) => (
                    <Form onSubmit={handleSubmit}>
                        <div className="wk-row">
                            <div className="wk-col-6">
                                <div className="custom-field-mb-10">
                                    <InputField
                                        label="Email ID"
                                        labelFor="username-2"
                                        size="small" indicator="required">
                                        <input
                                            type="text"
                                            id="username-2"
                                            name="username"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.username}
                                            readOnly
                                            disabled
                                            />
                                    </InputField>
                                </div>
                                <CheckboxField label="Reset Password">
                                    <input
                                        name="checkbox"
                                        type="checkbox"
                                        checked={!!resetPassword}
                                        onChange={(e: any) => {
                                            setResetPassword(!!e.target.checked)
                                            if (!e.target.checked) {
                                                values.password = "";
                                            }
                                        }}
                                    />
                                </CheckboxField>
                                <InputField
                                    label=""
                                    labelFor="password-3"
                                    size="small" indicator="required"
                                    status={!!errors.password && resetPassword ? "error" : "none"}
                                    errors={!!errors.password && resetPassword ? [{
                                        label: errors.password,
                                        labelId: 'label-error-id-3',
                                    }] : []}
                                >
                                    <input
                                        type="password"
                                        id="password-3"
                                        name="password"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.password}
                                        readOnly={!resetPassword}
                                        placeholder="Enter password"
                                        autoComplete="off"
                                    />
                                </InputField>
                                <SelectField
                                    label="Status"
                                    labelFor="select-id-1-5"
                                    size="small">
                                    <select id="select-id-1-5" name="active" value={values.active} onChange={handleChange}>
                                        <option value="" disabled>Select status</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </SelectField>
                            </div>
                            <div className="wk-col-6 custom-overflow-inherit">
                                <Multiselect
                                    items={projectList}
                                    pillLabel="Project selected"
                                    placeholder="Choose Project"
                                    size="small"
                                    label="Projects"
                                    indicator="required"
                                    ></Multiselect>
                                <Multiselect
                                    items={roleList}
                                    pillLabel="Roles selected"
                                    placeholder="Choose Role"
                                    size="small"
                                    label="Role"></Multiselect>

                            </div>
                        </div>
                        <div className="wk-row">
                            <div className="wk-col-12 text-right">
                                <ButtonField size={'small'}>
                                    <button type="submit" disabled={!(isValid)}>
                                        Update User
                                    </button>
                                </ButtonField>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default EditUser;
